import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";
import OfficerSelect from "./OfficerSelect.jsx";
import OfficerMultiSelect from "./OfficerMultiSelect.jsx";
import Dropdown from "./Dropdown.jsx";

const FUNDS = ["Revenue", "Water Supply", "Elementry"];

// Modal form for adding a single audit para manually.
export default function ParaForm({ onClose, onDone, para, onSuccess }) {
  const { t } = useI18n();
  const [form, setForm] = useState(() => {
    if (para) {
      return {
        s_no: para.s_no || "",
        audit_year: para.audit_year || "2023-2024",
        para_no: para.para_no || "",
        department: para.department || "",
        fund: para.fund || "",
        title: para.title || "",
        description: para.description || "",
        audit_remarks: para.audit_remarks || "",
        amount: para.amount || "",
        source: para.source || "MANUAL",
      };
    }
    return {
      s_no: "",
      audit_year: "2023-2024",
      para_no: "",
      department: "",
      fund: "",
      title: "",
      description: "",
      audit_remarks: "",
      amount: "",
      source: "MANUAL",
    };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [createdParaId, setCreatedParaId] = useState(para?.id || null);

  const { user } = useAuth();
  const [assignType, setAssignType] = useState(para?.assign_type || "UNASSIGNED");
  const [assignReason, setAssignReason] = useState(para?.assign_reason || "");
  const [officer, setOfficer] = useState(para?.assigned_officer || "");
  const [officers, setOfficers] = useState([]);
  const [zones, setZones] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [picked, setPicked] = useState(() => {
    if (para?.assign_type === "COMBINED" && para?.zone_links) {
      const p = {};
      para.zone_links.forEach((zl) => {
        p[zl.zone] = zl.cug_number || "";
      });
      return p;
    }
    return {};
  });
  const [pickedOfficers, setPickedOfficers] = useState(() => {
    if (para?.assign_type === "MULTIPLE" && para?.officer_links) {
      return para.officer_links.map((ol) => ol.officer);
    }
    return [];
  });

  const zoneLocked = user.role === "ZONAL_AC" && !!user.zone;

  useEffect(() => {
    // The main admin may assign to an officer (AE) or a Zonal AC. A zone
    // login may only pick officers posted in its own zone — never an AC.
    const params = {
      role: zoneLocked ? "OFFICER" : "OFFICER,ZONAL_AC",
      page_size: 100,
    };
    if (zoneLocked) params.zone = user.zone;
    api
      .get("/auth/users/", { params })
      .then((r) => setOfficers(r.data.results || r.data))
      .catch(() => { });
    api
      .get("/zones/")
      .then((r) => setZones(r.data.results || r.data))
      .catch(() => { });
    api
      .get("/departments/")
      .then((r) => setDepartments(r.data.results || r.data))
      .catch(() => { });
  }, [zoneLocked, user.zone]);

  const toggleZone = (z) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (z.id in next) delete next[z.id];
      else next[z.id] = z.cug_number || "";
      return next;
    });
  };

  const canSubmitAssign =
    assignType === "UNASSIGNED" ||
    (assignType === "INDIVIDUAL"
      ? !!officer
      : assignType === "MULTIPLE"
      ? pickedOfficers.length > 0
      : Object.keys(picked).length > 0);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const payload = {
        ...form,
        title: form.title || `Audit Para ${form.para_no}`,
        amount: form.amount === "" ? null : form.amount,
      };

      let activeParaId = createdParaId;
      if (!activeParaId) {
        const r = await api.post("/paras/", payload);
        activeParaId = r.data.id;
        setCreatedParaId(activeParaId);
      } else {
        await api.put(`/paras/${activeParaId}/`, payload);
      }

      if (assignType !== "UNASSIGNED") {
        const assignPayload = { assign_type: assignType, reason: assignReason };
        if (assignType === "INDIVIDUAL") {
          assignPayload.officer = Number(officer);
        } else if (assignType === "MULTIPLE") {
          assignPayload.officers = pickedOfficers;
        } else {
          assignPayload.zones = Object.entries(picked).map(([zone, cug]) => ({
            zone: Number(zone),
            cug_number: cug,
          }));
        }
        await api.post(`/paras/${activeParaId}/assign/`, assignPayload);
      }
      if (onDone) onDone();
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(
        e.response?.data?.detail ||
        JSON.stringify(e.response?.data || "Could not save/assign para.")
      );
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h3>{t("addPara")}</h3>
        {error && <div className="error-text">{error}</div>}

        <div className="row">
          <div className="field">
            <label>{t("sNo")}</label>
            <input value={form.s_no} onChange={set("s_no")} placeholder="e.g. 1" />
          </div>
          <div className="field">
            <label>{t("year")}</label>
            <input value={form.audit_year} onChange={set("audit_year")} />
          </div>
          <div className="field">
            <label>{t("paraNo")}</label>
            <input
              value={form.para_no}
              onChange={set("para_no")}
              placeholder="AP-109"
            />
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label>{t("department")}</label>
            <Dropdown
              value={form.department || ""}
              onChange={(v) => setForm({ ...form, department: v })}
              placeholder={`— ${t("selectDepartment")} —`}
              options={[
                { value: "", label: `— ${t("selectDepartment")} —` },
                ...departments.map((d) => {
                  const translationKey = "dept" + d.name.replace(/\s+/g, "");
                  const translated = t(translationKey);
                  const label = translated === translationKey ? d.name : translated;
                  return { value: d.name, label };
                }),
              ]}
            />
          </div>
          <div className="field">
            <label>Fund</label>
            <Dropdown
              value={form.fund || ""}
              onChange={(v) => setForm({ ...form, fund: v })}
              placeholder="— Select Fund —"
              options={[
                { value: "", label: "— Select Fund —" },
                ...FUNDS.map((f) => ({ value: f, label: f })),
              ]}
            />
          </div>
          <div className="field">
            <label>{t("amount")} (₹)</label>
            <input
              type="number"
              value={form.amount}
              onChange={set("amount")}
            />
          </div>
        </div>
        <div className="field">
          <label>{t("description")}</label>
          <textarea value={form.description} onChange={set("description")} />
        </div>

        <div className="assign-section-header">
          <h4 style={{ margin: 0, color: "#1e3a8a", fontSize: "13.5px", fontWeight: 700 }}>
            🔗 {t("assignPara")}
          </h4>
          <div className="assigning-as-chip">
            👤 Assigning as: <strong style={{ color: "#0f172a" }}>{user.full_name}</strong>
          </div>
        </div>


        <div className="field">
          <label>{t("assignType")}</label>
          <Dropdown
            value={assignType}
            onChange={(v) => setAssignType(v)}
            placeholder={t("notAssigned")}
            options={[
              { value: "UNASSIGNED", label: t("notAssigned") },
              { value: "INDIVIDUAL", label: t("individual") },
              { value: "MULTIPLE", label: "Multiple Officers" },
              { value: "COMBINED", label: t("combined") },
            ]}
          />
        </div>

        {assignType === "INDIVIDUAL" && (
          <div className="field">
            <label>{t("selectOfficer")}</label>
            {zoneLocked && (
              <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
                Showing officers in your zone only.
              </div>
            )}
            <OfficerSelect
              officers={officers}
              value={officer}
              onChange={setOfficer}
            />
          </div>
        )}

        {assignType === "MULTIPLE" && (
          <div className="field">
            <label>Select Officers — each tracks their own status</label>
            <OfficerMultiSelect
              officers={officers}
              value={pickedOfficers}
              onChange={setPickedOfficers}
            />
          </div>
        )}

        {assignType === "COMBINED" && (
          <div className="field">
            <label>
              {t("selectZones")} — {t("cugNumber")}
            </label>
            <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 }}>
              {zones.map((z) => (
                <div
                  key={z.id}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <input
                    type="checkbox"
                    style={{ width: 16 }}
                    checked={z.id in picked}
                    onChange={() => toggleZone(z)}
                  />
                  <span style={{ flex: 1 }}>
                    {z.name} ({z.code})
                  </span>
                  <input
                    style={{ width: 130, padding: "4px 8px" }}
                    placeholder={t("cugNumber")}
                    disabled={!(z.id in picked)}
                    value={picked[z.id] || ""}
                    onChange={(e) =>
                      setPicked((p) => ({ ...p, [z.id]: e.target.value }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {assignType !== "UNASSIGNED" && (
          <div className="field">
            <label>{t("assignReason")}</label>
            <textarea
              value={assignReason}
              onChange={(e) => setAssignReason(e.target.value)}
              placeholder={t("reason")}
              style={{ minHeight: 60 }}
            />
          </div>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            className="btn btn-primary"
            disabled={busy || !form.para_no || !form.department || !canSubmitAssign}
            onClick={submit}
          >
            {t("save")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
